<template>
    <div class="flex size-full flex-col justify-stretch">
        <toast-portal />
        <router-view />
        <rate-limiting-modal />
        <vue-query-devtools />
    </div>
</template>

<script setup lang="ts">
import whiteLabelHelper from './api/helpers/whiteLabelHelper.js';
import {storeToRefs} from 'pinia';
import {useAccountStore} from './stores/accountStore';
import {useGlobalStore} from './stores/globalStore';
import ToastPortal from './shared/components/toast/ToastPortal.vue';
import {useScriptLoader} from './shared/composables/useScriptLoader';
import {watch} from 'vue';
import RateLimitingModal from './shared/components/RateLimitingModal.vue';
import {useUserStore} from './stores/userStore.js';
import {setMatomoUserId} from '@/shared/matomoTracking';
import {setUser as setSentryUser} from '@sentry/vue';
import {VueQueryDevtools} from '@tanstack/vue-query-devtools';
import {useSubDomain} from '@/shared/composables/useSubdomain';
import {useWhitelabelSettingsQuery} from '@/api/queries/queries/useWhitelabelSettingsQuery';
import {checkAndRedirect} from '@/shared/composables/useSubdomainRedirect';

useGlobalStore().init();
useScriptLoader();

const {account} = storeToRefs(useAccountStore());
const {user} = storeToRefs(useUserStore());

watch(
    account,
    async accountValue => {
        whiteLabelHelper.methods.whiteLabelCheck(accountValue);

        // Check for potential subdomain redirect on account value change
        await checkAndRedirect();
    },
    {immediate: true}
);

useWhitelabelSettings();

watch(() => user.value?.id, onUserChanged, {immediate: true});

function onUserChanged(userId: number | undefined) {
    setMatomoUserId(userId ? String(userId) : undefined);
    setSentryUser(userId ? {id: String(userId), account_id: String(account.value?.id)} : null);
}

function useWhitelabelSettings() {
    const {subDomain} = useSubDomain();
    const accountStore = useAccountStore();
    const {data} = useWhitelabelSettingsQuery(subDomain);

    watch(
        data,
        newValue => {
            whiteLabelHelper.methods.whiteLabelCheck(newValue);

            const account = accountStore.account;
            //prevent account update when no account is set
            if (!account) {
                return;
            }

            const updatedAccount = Object.assign({}, account, newValue);
            accountStore.updateAccount(updatedAccount);
        },
        {immediate: true}
    );
}
</script>
